import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { EyeIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import React from 'react';

import { Spinner } from '@oysterjs/ui/spinner';

/**
 * Floating action bar that appears at the bottom of a library list page when
 * any row is selected. Shared by styles, templates, word templates, custom
 * forms, and outbound Calls so bulk action UI stays identical.
 */
const selectionActionClass = (disabled: boolean) =>
  clsx(
    'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
    'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
    !disabled &&
      'text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-100/50 dark:hover:bg-white/10',
    disabled &&
      'text-neutral-500 dark:text-neutral-400 bg-neutral-50/50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 border-dashed cursor-default'
  );

export type FloatingSelectionExtraAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
};

export function FloatingSelectionBar({
  count,
  onClear,
  onVisibility,
  onArchive,
  isArchiving,
  isUpdatingVisibility,
  visibilityEnabled,
  archiveLabel = 'Archive',
  archiveIcon,
  archiveEnabled = true,
  extraAction,
  extraActions,
  note
}: {
  count: number;
  onClear: () => void;
  onVisibility?: () => void;
  onArchive: () => void;
  isArchiving: boolean;
  isUpdatingVisibility?: boolean;
  visibilityEnabled?: boolean;
  archiveLabel?: string;
  /** Replaces the trash icon, for a view whose primary action is a restore. */
  archiveIcon?: React.ReactNode;
  archiveEnabled?: boolean;
  /** Optional secondary bulk action (e.g. Cancel calls on outbound). */
  extraAction?: FloatingSelectionExtraAction;
  /** Additional bulk actions rendered before archive (after optional extraAction). */
  extraActions?: FloatingSelectionExtraAction[];
  /** Shown beside the count, so a disabled action can say why in the bar itself. */
  note?: string | null;
}) {
  const archiveDisabled = isArchiving || !archiveEnabled;
  const allExtraActions = [...(extraAction ? [extraAction] : []), ...(extraActions ?? [])];

  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.div
          className="fixed left-1/2 lg:left-[calc(8rem+50vw)] bottom-12 shadow-xl border border-neutral-100 dark:border-neutral-700 rounded-full bg-white dark:bg-neutral-800 p-2 z-10 flex gap-2"
          initial={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
          animate={{ opacity: 1, translateX: '-50%', translateY: 0, scale: 1 }}
          exit={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
          transition={{ duration: 0.15, ease: 'easeInOut' }}
        >
          <Headless.Button
            className={clsx(
              'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
              'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
              'text-white bg-blue-600/80 border border-transparent hover:bg-blue-600/90'
            )}
            onClick={onClear}
          >
            <div>{count} selected</div>
            <div>
              <XMarkIcon className="size-3.5" />
            </div>
          </Headless.Button>
          {note && (
            <span className="self-center px-1 text-xs text-neutral-500 dark:text-neutral-400">
              {note}
            </span>
          )}
          {visibilityEnabled && onVisibility && (
            <Headless.Button
              className={selectionActionClass(!!isUpdatingVisibility)}
              onClick={onVisibility}
              disabled={isUpdatingVisibility}
              title="Change visibility"
            >
              <EyeIcon className="size-3.5" />
              Visibility
              {isUpdatingVisibility && <Spinner size="size-3.5" color="neutral" />}
            </Headless.Button>
          )}
          {allExtraActions.map((action) => {
            const disabled = !!action.disabled || !!action.loading;
            return (
              <Headless.Button
                key={action.label}
                className={selectionActionClass(disabled)}
                onClick={action.onClick}
                disabled={disabled}
              >
                {action.icon ?? <TrashIcon className="size-3.5" />}
                {action.label}
                {action.loading && <Spinner size="size-3.5" color="neutral" />}
              </Headless.Button>
            );
          })}
          <Headless.Button
            className={selectionActionClass(archiveDisabled)}
            onClick={onArchive}
            disabled={archiveDisabled}
          >
            {archiveIcon ?? <TrashIcon className="size-3.5" />}
            {archiveLabel}
            {isArchiving && <Spinner size="size-3.5" color="neutral" />}
          </Headless.Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
